import React, { useCallback, useEffect } from 'react';
import { CardViewContentBlockProps } from './types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../../common/library-category-card/LibraryCategoryCard';
import { useTranslation } from 'react-i18next';
import TerminationIcon from '../../../../../assets/svg/termination.svg?react';
import PolicyIcon from '../../../../../assets/svg/policy.svg?react';
import ProposalIcon from '../../../../../assets/svg/proposal.svg?react';
import ProjectMaterialsIcon from '../../../../../assets/svg/project-materials.svg?react';
import InvoicingIcon from '../../../../../assets/svg/invoicing.svg?react';
import TendersIcon from '../../../../../assets/svg/tenders.svg?react';
import { TAGS_LABELS } from '../../../../common/constants/tags-labels';
import { Helpers } from '../../../../common/helpers/helpers';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';

export const CustomersCategoryContent = ({ templates, setWorkflow }: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const [searchParam] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workflowItem = searchParam.get('workflow');
  const templatesFilteredByCategory = [...templates].filter(
    (template) =>
      'dl_workflows' in template && 'is_free' in template && template.category === 'Customers'
  );
  const company = useAppSelector(selectAllCompanies)[0];
  const getTemplatesCount = (workflow: string) => {
    return templatesFilteredByCategory.filter((el) => el.dl_workflows.includes(workflow)).length;
  };
  const getFreeTemplatesCount = (workflow: string) => {
    return templatesFilteredByCategory.filter(
      (el) => el.dl_workflows.includes(workflow) && el.is_free
    ).length;
  };
  const getTemplatesCountByWorkflow = (workflow: string) => {
    return templatesFilteredByCategory.filter((el) => el.dl_workflows.includes(workflow)).length;
  };
  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.customer.agreement'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.customer.proposalOrQuote'):
        return <ProposalIcon />;
      case t('tagsLabels.documentWorkflow.customer.customerProjectMaterials'):
        return <ProjectMaterialsIcon />;
      case t('tagsLabels.documentWorkflow.customer.invoicing'):
        return <InvoicingIcon />;
      case t('tagsLabels.documentWorkflow.customer.tenders'):
        return <TendersIcon />;
      case t('tagsLabels.documentWorkflow.customer.termination'):
        return <TerminationIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.customer.proposalOrQuote'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.customer.agreement'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.customer.invoicing'):
        return '3. ';
    }
  };
  const handleSetWorkflow = useCallback(() => {
    if (workflowItem) {
      setWorkflow(decodeURI(workflowItem));
      navigate(location.pathname);
    }
  }, [workflowItem, setWorkflow, navigate, location.pathname]);

  useEffect(() => {
    handleSetWorkflow();
  }, [handleSetWorkflow]);

  return (
    <>
      <Typography
        variant='h3'
        component='h3'
        sx={{ marginBottom: '16px', fontSize: '15px', lineHeight: '24px' }}
      >
        {t('headers.workflows')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {TAGS_LABELS.documentWorkflow.customer.map((el) =>
          getTemplatesCountByWorkflow(t(el)) > 0 ? (
            <LibraryCategoryCard
              key={el}
              title={Helpers.capitalizeTitle(getWorkflowNumber(t(el)) + t(el))}
              description={
                getTemplatesCount(t(el)) +
                (getTemplatesCount(t(el)) > 1 ? ' templates' : ' template')
              }
              hasFreeLabel={
                getFreeTemplatesCount(t(el)) > 0 &&
                !company?.stripe_subscription &&
                !company?.stripe_product_id
              }
              onClick={() => setWorkflow(t(el))}
              icon={getIcon(t(el))}
            />
          ) : null
        )}
      </Box>
    </>
  );
};
